import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { FolderOpen, Upload, Download, FileText, Loader2, Users, ShieldAlert, EyeOff, Trash2, Info } from "lucide-react";

/**
 * CommunityDocsPanel — shared folder for Hot Mic members.
 * • Any authenticated member can upload + name a doc.
 * • Nobody can delete their own upload — only admins can.
 * • Every doc renders with a "Community share" tag + uploader byline.
 *
 * Set `adminMode` to true from the admin panel to enable manage actions.
 */
export default function CommunityDocsPanel({ adminMode = false }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const reload = () => {
    setLoading(true);
    api.get(adminMode ? "/admin/community-docs" : "/community-docs")
      .then((r) => setDocs(r.data.docs || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { reload(); }, [adminMode]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (!file || !name.trim()) {
      toast.error("Give the document a name and pick a file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("display_name", name.trim());
      if (description.trim()) fd.append("description", description.trim());
      await api.post("/community-docs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks for sharing — your document is live in the community folder.");
      setName(""); setDescription(""); setFile(null); if (fileRef.current) fileRef.current.value = "";
      setShowUpload(false);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally { setUploading(false); }
  };

  const download = async (doc) => {
    try {
      const token = localStorage.getItem("pjp_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/community-docs/${doc.id}/download`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = doc.original_filename || doc.display_name || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Download failed."); }
  };

  const setStatus = async (doc, status) => {
    try {
      await api.patch(`/admin/community-docs/${doc.id}`, { status });
      toast.success(`Set to ${status}.`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed."); }
  };
  const hardDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.display_name}" permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/community-docs/${doc.id}`);
      toast.success("Deleted.");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed."); }
  };

  const fmtBytes = (n) => {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div className="rounded-2xl border p-5" data-testid="community-docs-panel"
      style={{ borderColor: "rgba(201,155,255,0.35)", background: "linear-gradient(160deg, rgba(201,155,255,0.10) 0%, rgba(5,8,16,0.9) 60%)" }}>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4" style={{ color: "#C99BFF" }} />
          <div className="mono text-[10px] tracking-[0.24em] uppercase" style={{ color: "#C99BFF" }}>
            // COMMUNITY SHARE · DOCUMENTS
          </div>
        </div>
        {!adminMode && (
          <button
            type="button"
            onClick={() => setShowUpload(!showUpload)}
            data-testid="community-docs-toggle-upload"
            className="mono text-[10.5px] tracking-widest uppercase px-3 py-1.5 rounded-full border border-white/15 hover:bg-white/5 text-white/80 inline-flex items-center gap-1.5"
          >
            <Upload className="h-3 w-3" /> Share a document
          </button>
        )}
      </div>

      {/* Community banner — legal/etiquette hint */}
      <div className="rounded-lg border border-white/10 bg-black/40 px-3 py-2 mb-4 flex items-start gap-2">
        <Info className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" style={{ color: "#C99BFF" }} />
        <div className="text-[11.5px] text-white/70 leading-relaxed">
          These are <b className="text-white">community share documents</b> — training PDFs, checklists, cheat sheets, POH extracts, etc. that members upload for everyone. Only PJP admins can remove them, so please only share things you have the rights to distribute.
        </div>
      </div>

      {/* Upload form */}
      {showUpload && !adminMode && (
        <div className="rounded-xl border border-white/10 bg-black/40 p-4 mb-4" data-testid="community-docs-upload-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Document name</div>
              <input value={name} onChange={(e) => setName(e.target.value)} className="input w-full" placeholder='e.g. "G550 quick-ref checklist"' data-testid="community-docs-name" />
            </div>
            <div>
              <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">Description (optional)</div>
              <input value={description} onChange={(e) => setDescription(e.target.value)} className="input w-full" placeholder="What is this useful for?" data-testid="community-docs-desc" />
            </div>
          </div>
          <div className="mt-3">
            <div className="mono text-[10px] tracking-widest uppercase text-white/50 mb-1">File · PDF / DOCX / XLSX / PNG · max 200 MB</div>
            <input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-[13px] text-white/80 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-cyan file:text-obsidian file:font-bold file:text-[11px] file:tracking-widest file:uppercase" data-testid="community-docs-file" />
          </div>
          <div className="mt-4 flex gap-2 justify-end">
            <button type="button" onClick={() => setShowUpload(false)} className="btn-hud">Cancel</button>
            <button type="button" onClick={submit} disabled={uploading || !file || !name.trim()} data-testid="community-docs-submit" className="btn-hud btn-hud-solid">
              {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5" /> Share to community</>}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-cyan" /></div>
      ) : docs.length === 0 ? (
        <div className="text-center py-6 text-white/50 text-[13px]">
          No community documents yet. Be the first to share.
        </div>
      ) : (
        <div className="space-y-2" data-testid="community-docs-list">
          {docs.map((d) => (
            <div key={d.id} className="rounded-lg border border-white/10 bg-black/40 p-3 flex items-start gap-3">
              <div className="h-9 w-9 rounded flex-shrink-0 grid place-items-center" style={{ background: "rgba(201,155,255,0.15)", border: "1px solid rgba(201,155,255,0.4)" }}>
                <FileText className="h-4 w-4" style={{ color: "#C99BFF" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-semibold text-white text-[14px] truncate">{d.display_name}</div>
                  <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded" style={{ background: "rgba(201,155,255,0.15)", color: "#C99BFF", border: "1px solid rgba(201,155,255,0.35)" }}>
                    COMMUNITY SHARE
                  </span>
                  {adminMode && d.status !== "active" && (
                    <span className="mono text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded" style={{ background: "rgba(255,80,80,0.15)", color: "#FF7A7A", border: "1px solid rgba(255,80,80,0.4)" }}>
                      {d.status}
                    </span>
                  )}
                </div>
                {d.description && <div className="text-[12px] text-white/60 mt-0.5">{d.description}</div>}
                <div className="mono text-[10px] text-white/50 mt-1 flex items-center gap-2 flex-wrap">
                  <Users className="h-3 w-3" />
                  <span>Shared by <b className="text-white/85">{d.uploader_name}</b></span>
                  {adminMode && <span className="text-white/40">· {d.uploader_email}</span>}
                  <span>· {fmtBytes(d.size)} · {d.download_count || 0} downloads</span>
                  <span>· {(d.created_at || "").slice(0, 10)}</span>
                </div>
              </div>
              <div className="flex-shrink-0 flex items-center gap-1">
                <button type="button" onClick={() => download(d)} data-testid={`community-docs-dl-${d.id}`} title="Download" className="mono text-[10px] tracking-widest uppercase px-2 py-1.5 rounded border border-white/15 text-white/80 hover:bg-white/5 inline-flex items-center gap-1">
                  <Download className="h-3 w-3" /> DL
                </button>
                {adminMode && (
                  <>
                    <button type="button" onClick={() => setStatus(d, d.status === "hidden" ? "active" : "hidden")} title={d.status === "hidden" ? "Restore" : "Hide"} data-testid={`admin-doc-hide-${d.id}`} className="mono text-[10px] px-2 py-1.5 rounded border border-white/15 text-amber-300 hover:bg-white/5">
                      <EyeOff className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => hardDelete(d)} title="Delete permanently" data-testid={`admin-doc-delete-${d.id}`} className="mono text-[10px] px-2 py-1.5 rounded border border-red-400/40 text-red-300 hover:bg-red-500/10">
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
